/**
 * WorkflowEdit Page
 *
 * Edit an existing workflow with steps
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams, useBlocker } from 'react-router-dom'
import {
  GitBranch,
  ArrowLeft,
  Save,
  Loader2,
  AlertTriangle,
} from 'lucide-react'

import { Button, Alert, Modal } from '@components/common'
import { WorkflowStepBuilder } from '@components/features/workflows'
import useWorkflowStore from '@store/useWorkflowStore'
import { useAuth } from '@context/AuthContext'

import './WorkflowForm.scss'

const WorkflowEdit = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { user } = useAuth()

  // Store
  const {
    selectedWorkflow: currentWorkflow,
    companyUsers,
    loading: isLoading,
    error,
    fetchWorkflowById,
    updateWorkflow,
    fetchCompanyUsers,
    clearSelectedWorkflow,
  } = useWorkflowStore()

  // Clear error function - local since store doesn't have one
  const clearError = () => {}

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  })
  const [steps, setSteps] = useState([])
  const [formErrors, setFormErrors] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [showExitModal, setShowExitModal] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState(null)
  const [initialDataLoaded, setInitialDataLoaded] = useState(false)

  // Block navigation when there are unsaved changes
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
  )

  // Handle blocker state changes
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setPendingNavigation(blocker)
      setShowExitModal(true)
    }
  }, [blocker.state])

  // Get company ID from authenticated user
  // FALLBACK: If user is null in AuthContext, try to get from storage directly
  let companyId = user?.company_id

  if (!companyId) {
    try {
      const userDataStr = sessionStorage.getItem('auth_user_data') || localStorage.getItem('auth_user_data')
      if (userDataStr) {
        const userData = JSON.parse(userDataStr)
        companyId = userData?.company_id
        console.warn('⚠️ WorkflowEdit: AuthContext user is null, using company_id from storage:', companyId)
      }
    } catch (error) {
      console.error('WorkflowEdit: Error parsing user data from storage:', error)
    }
  }

  // Fetch workflow data and company users on mount
  useEffect(() => {
    const loadData = async () => {
      if (!companyId) return

      await Promise.all([fetchWorkflowById(id), fetchCompanyUsers(companyId)])
    }

    loadData()
  }, [id, companyId, fetchWorkflowById, fetchCompanyUsers])

  // Populate form when workflow data loads
  useEffect(() => {
    if (currentWorkflow && !initialDataLoaded) {
      console.log('🔍 WorkflowEdit - Loading workflow data:', currentWorkflow)
      console.log('🔍 WorkflowEdit - Steps from API:', currentWorkflow.steps)

      setFormData({
        name: currentWorkflow.name || '',
        description: currentWorkflow.description || '',
      })

      // Map steps to include temporary IDs for drag and drop
      // IMPORTANT: Backend returns role_slug, but frontend expects role
      const mappedSteps = (currentWorkflow.steps || []).map((step, index) => {
        const mapped = {
          id: step.id || `step-${index}`,
          name: step.name || '',
          role: step.role || step.role_slug || '', // Backend returns role_slug
          approver_id: step.approver_id ? parseInt(step.approver_id) : null,
          sla_hours: parseInt(step.sla_hours) || 24,
          evidence_required: step.evidence_required === '1' || step.evidence_required === 1 || step.evidence_required === true,
        }
        console.log(`🔍 WorkflowEdit - Mapped step ${index + 1}:`, mapped)
        return mapped
      })

      console.log('🔍 WorkflowEdit - All mapped steps:', mappedSteps)
      setSteps(mappedSteps)
      setInitialDataLoaded(true)
    }
  }, [currentWorkflow, initialDataLoaded])

  // Track changes
  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setHasUnsavedChanges(true)

    // Clear field error
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: null }))
    }
  }, [formErrors])

  const handleStepsChange = useCallback((newSteps) => {
    setSteps(newSteps)
    setHasUnsavedChanges(true)

    // Clear steps error
    if (formErrors.steps) {
      setFormErrors((prev) => ({ ...prev, steps: null }))
    }
  }, [formErrors])

  // Validate form
  const validateForm = () => {
    const errors = {}

    if (!formData.name.trim()) {
      errors.name = 'Workflow name is required'
    } else if (formData.name.length < 2) {
      errors.name = 'Workflow name must be at least 2 characters'
    }

    if (steps.length === 0) {
      errors.steps = 'At least one workflow step is required'
    } else {
      // Check for incomplete steps
      const incompleteSteps = steps.filter(
        (step) => !step.name.trim() || !step.role || !step.approver_id
      )
      if (incompleteSteps.length > 0) {
        errors.steps = 'All steps must have a name, role, and approver'
      }
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSaving(true)
    clearError()

    try {
      // Prepare workflow data
      const workflowData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
      }

      // Prepare steps data
      const stepsData = steps.map((step, index) => ({
        name: step.name.trim(),
        role: step.role,
        approver_id: parseInt(step.approver_id, 10),
        sla_hours: parseInt(step.sla_hours, 10) || 24,
        evidence_required: step.evidence_required ? 1 : 0,
        step_number: index + 1,
      }))

      await updateWorkflow(id, { ...workflowData, steps: stepsData })
      setHasUnsavedChanges(false)
      navigate('/workflows')
    } catch (err) {
      console.error('Failed to update workflow:', err)
    } finally {
      setIsSaving(false)
    }
  }

  // Handle back navigation
  const handleBack = () => {
    if (hasUnsavedChanges) {
      setShowExitModal(true)
    } else {
      navigate('/workflows')
    }
  }

  // Handle exit modal actions
  const handleDiscardChanges = () => {
    setShowExitModal(false)
    setHasUnsavedChanges(false)

    if (pendingNavigation) {
      pendingNavigation.proceed()
      setPendingNavigation(null)
    } else {
      navigate('/workflows')
    }
  }

  const handleCancelExit = () => {
    setShowExitModal(false)
    if (pendingNavigation) {
      pendingNavigation.reset()
      setPendingNavigation(null)
    }
  }

  // Loading state
  if (isLoading && !initialDataLoaded) {
    return (
      <div className="workflow-form-page">
        <div className="loading-container">
          <Loader2 className="spin" size={40} />
          <p>Loading workflow...</p>
        </div>
      </div>
    )
  }

  // Not found state
  if (!isLoading && !currentWorkflow && initialDataLoaded) {
    return (
      <div className="workflow-form-page">
        <Alert
          type="error"
          title="Workflow Not Found"
          message="The workflow you're looking for doesn't exist or has been deleted."
        />
        <div style={{ marginTop: 'var(--spacing-lg)' }}>
          <Button variant="secondary" onClick={() => navigate('/workflows')}>
            <ArrowLeft size={16} /> Back to Workflows
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="workflow-form-page">
      {/* Header */}
      <div className="workflow-form-header">
        <div className="workflow-form-header__left">
          <button className="workflow-form-header__back" onClick={handleBack}>
            <ArrowLeft size={16} /> Back to Workflows
          </button>
          <div className="workflow-form-header__title-row">
            <div className="workflow-form-header__icon">
              <GitBranch size={28} />
            </div>
            <div className="workflow-form-header__info">
              <h1 className="workflow-form-header__title">Edit Workflow</h1>
              <p className="workflow-form-header__subtitle">
                Modify the workflow steps and settings
              </p>
            </div>
          </div>
        </div>
        <div className="workflow-form-header__actions">
          {hasUnsavedChanges && (
            <span className="workflow-form-header__unsaved">Unsaved changes</span>
          )}
          <Button variant="secondary" onClick={handleBack} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="spin" size={16} /> Saving...
              </>
            ) : (
              <>
                <Save size={16} /> Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="workflow-form__alert">
          <Alert type="error" title="Error" message={error} dismissible onDismiss={clearError} />
        </div>
      )}

      {/* Form */}
      <form className="workflow-form" onSubmit={handleSubmit}>
        {/* Basic Details Section */}
        <div className="workflow-form__section">
          <h2 className="workflow-form__section-title">Basic Details</h2>

          <div className="workflow-form__field">
            <label htmlFor="name">
              Workflow Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              placeholder="Enter workflow name"
              className={formErrors.name ? 'error' : ''}
              disabled={isSaving}
            />
            {formErrors.name && <span className="field-error">{formErrors.name}</span>}
          </div>

          <div className="workflow-form__field">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Enter workflow description (optional)"
              rows={3}
              disabled={isSaving}
            />
          </div>

        </div>

        {/* Workflow Steps Section */}
        <div className="workflow-form__section">
          <h2 className="workflow-form__section-title">Workflow Steps</h2>

          {formErrors.steps && (
            <Alert
              type="warning"
              message={formErrors.steps}
              style={{ marginBottom: 'var(--spacing-md)' }}
            />
          )}

          <WorkflowStepBuilder
            steps={steps}
            onChange={handleStepsChange}
            users={companyUsers}
            disabled={isSaving}
          />
        </div>
      </form>

      {/* Exit Confirmation Modal */}
      <Modal
        isOpen={showExitModal}
        onClose={handleCancelExit}
        title="Unsaved Changes"
        size="small"
        closeOnBackdrop={false}
      >
        <div className="exit-modal">
          <div className="exit-modal__icon">
            <AlertTriangle size={48} />
          </div>
          <div className="exit-modal__content">
            <p>You have unsaved changes that will be lost.</p>
            <p>Are you sure you want to leave this page?</p>
          </div>
          <div className="exit-modal__actions">
            <Button variant="secondary" onClick={handleCancelExit}>
              Keep Editing
            </Button>
            <Button variant="danger" onClick={handleDiscardChanges}>
              Discard Changes
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default WorkflowEdit
